import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useUser } from './UserContext';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { user } = useUser();
    const [cart, setCart] = useState([]);
    const [cartCount, setCartCount] = useState(0);
    const prevUserRef = useRef();

    // Load cart from localStorage on init
    useEffect(() => {
        const savedCart = localStorage.getItem('showroom_cart');
        if (savedCart) {
            try {
                const parsedCart = JSON.parse(savedCart);
                setCart(parsedCart);
            } catch (e) {
                console.error("Failed to parse cart from localStorage", e);
            }
        }
    }, []);

    // Clear cart on logout
    useEffect(() => {
        // If there was a user and now there isn't, clear the cart
        if (prevUserRef.current && !user) {
            setCart([]);
            localStorage.removeItem('showroom_cart');
        }
        prevUserRef.current = user;
    }, [user]);

    // Update cart count and save to localStorage
    useEffect(() => {
        const count = cart.reduce((total, item) => total + item.quantity, 0);
        setCartCount(count);
        localStorage.setItem('showroom_cart', JSON.stringify(cart));
    }, [cart]);

    const addToCart = (product, quantity = 1) => {
        let wasAdded = false;
        // Check if product is in stock
        if (product.stock === 0) return false;

        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === product.id);
            if (existingItem) {
                wasAdded = false;
                return prevCart; // Don't add if already exists
            }
            wasAdded = true;
            // Limit requested quantity to available stock
            const finalQuantity = Math.min(quantity, product.stock || quantity);
            return [...prevCart, { ...product, quantity: finalQuantity }];
        });
        return wasAdded;
    };

    const removeFromCart = (productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity < 1) return;
        setCart(prevCart =>
            prevCart.map(item => {
                if (item.id === productId) {
                    // Enforce stock limit
                    const finalQuantity = Math.min(quantity, item.stock || quantity);
                    return { ...item, quantity: finalQuantity };
                }
                return item;
            })
        );
    };

    const clearCart = () => {
        setCart([]);
    };

    const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);

    return (
        <CartContext.Provider value={{
            cart,
            cartCount,
            cartTotal,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart
        }}>
            {children}
        </CartContext.Provider>
    );
};
